import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import api from "@/lib/api";
import useApiData from "@/hooks/useApiData";
import FilterBar from "@/components/FilterBar";
import ListState from "@/components/ListState";
import EventCard from "@/components/events/EventCard";

const EVENT_TYPES = [
  { id: "all", label: "All Events" },
  { id: "tournament", label: "Tournaments" },
  { id: "clinic", label: "Clinics" },
  { id: "league", label: "Leagues" },
  { id: "social", label: "Social" },
];

const groupByMonth = (events) =>
  events.reduce((acc, event) => {
    const monthYear = new Date(event.date).toLocaleString("default", { month: "long", year: "numeric" });
    (acc[monthYear] ||= []).push(event);
    return acc;
  }, {});

const Events = () => {
  const [activeType, setActiveType] = useState("all");
  const { data: events, loading } = useApiData(api.getEvents, activeType === "all" ? {} : { event_type: activeType });
  const grouped = groupByMonth(events);

  return (
    <div className="pt-16" data-testid="events-page" style={{ background: "var(--lrg-bg)" }}>
      <section className="py-16" style={{ background: "var(--lrg-surface)" }} data-testid="events-hero">
        <div className="container-custom">
          <div className="overline mb-4">Calendar</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F2EDE4] mb-3">Events</h1>
          <p className="text-[#F2EDE4]/30 max-w-md">Tournaments, clinics, leagues, and social events at LRG Squash.</p>
        </div>
      </section>

      <FilterBar options={EVENT_TYPES} active={activeType} onChange={setActiveType} testPrefix="filter-type" />

      <section className="section" data-testid="events-list">
        <div className="container-custom">
          <ListState loading={loading} isEmpty={events.length === 0} loadingText="Loading events..." emptyText="No events found. Try adjusting your filters.">
            <div className="space-y-12">
              {Object.entries(grouped).map(([monthYear, monthEvents]) => (
                <div key={monthYear}>
                  <h2 className="font-display text-2xl font-bold text-[#F2EDE4] mb-6 flex items-center gap-2">
                    <CalendarIcon className="w-6 h-6 text-[#0D6B56]" /> {monthYear}
                  </h2>
                  <div className="space-y-4">
                    {monthEvents.map((event) => <EventCard key={event.id} event={event} />)}
                  </div>
                </div>
              ))}
            </div>
          </ListState>
        </div>
      </section>

      <section className="section" style={{ background: "var(--lrg-surface)" }} data-testid="events-submit-cta">
        <div className="container-custom flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#F2EDE4] mb-2">Hosting a Squash Event?</h3>
            <p className="text-[#F2EDE4]/50">Submit your event for consideration to be added to our calendar.</p>
          </div>
          <a href="/contact" className="btn-primary whitespace-nowrap" data-testid="events-submit-event">Submit Event</a>
        </div>
      </section>
    </div>
  );
};

export default Events;
